import { Button } from '@cargoflow/ui/components/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@cargoflow/ui/components/tabs'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { match } from 'ts-pattern'
import { DeletedActions, LiveActions } from '@/features/companies/company-actions'
import { PurgeCompanyDialog, SoftDeleteCompanyDialog } from '@/features/companies/company-dialogs'
import { CompanyDetailsTab } from '@/features/companies/details-tab'
import { CompanyLoadsTab } from '@/features/companies/loads-tab'
import { StatusBadge } from '@/features/companies/status-badge'
import { VerifyDialog } from '@/features/companies/verify-dialog'
import { useCompanyActions } from '@/hooks/use-company-actions'
import { api } from '@/lib/api'
import { formatRelative } from '@/lib/format'
import { queryKeys } from '@/lib/query-keys'

export const Route = createFileRoute('/_authenticated/companies/$company_id')({
  component: CompanyDetail,
})

function CompanyDetail() {
  const { company_id } = Route.useParams()
  const [dialogAction, setDialogAction] = useState<'approve' | 'reject' | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [purgeOpen, setPurgeOpen] = useState(false)

  const actions = useCompanyActions(company_id)
  const { vatLookup, softDelete, restore, purge, resendVerification, downloadDocument } = actions

  const {
    data: company,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.companies.detail(company_id),
    queryFn: async () => {
      const { data, error } = await api.admin.companies({ company_id }).get()
      if (error) throw new Error(typeof error.value === 'string' ? error.value : 'Failed to load')
      return data
    },
  })

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    )
  }

  if (error || !company) {
    return (
      <div className="text-destructive text-center">
        {(error as Error)?.message ?? 'Company not found'}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" asChild className="-ml-3">
            <Link to="/companies">
              <ArrowLeft className="h-4 w-4" /> Back to list
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">{company.name}</h1>
            <StatusBadge status={company.verification_status} />
          </div>
        </div>
        <div className="flex gap-2">
          {match(company.deleted_at !== null)
            .with(true, () => (
              <DeletedActions
                isRestorePending={restore.isPending}
                onRestore={() => restore.mutate()}
                onPurge={() => setPurgeOpen(true)}
              />
            ))
            .with(false, () => (
              <LiveActions
                cui={company.cui}
                isVatLookupPending={vatLookup.isPending}
                onVatLookup={() => vatLookup.mutate()}
                onReject={() => setDialogAction('reject')}
                onApprove={() => setDialogAction('approve')}
                onDelete={() => setDeleteOpen(true)}
              />
            ))
            .exhaustive()}
        </div>
      </div>

      {company.deleted_at && (
        <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <div className="font-semibold">This company is soft-deleted.</div>
            <div className="text-amber-800">
              Hidden from operational queries since {formatRelative(company.deleted_at)}. Sessions
              were dropped. Restore to bring it back or purge to wipe every trace.
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="loads">Loads</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-4">
          <CompanyDetailsTab
            company={company}
            isResendPending={resendVerification.isPending}
            onResendVerification={() => resendVerification.mutate()}
            isDownloadPending={downloadDocument.isPending}
            onDownloadDocument={(documentId) => downloadDocument.mutate(documentId)}
          />
        </TabsContent>

        <TabsContent value="loads" className="mt-4">
          <CompanyLoadsTab companyId={company.id} />
        </TabsContent>
      </Tabs>

      <VerifyDialog
        companyId={company.id}
        action={dialogAction ?? 'approve'}
        open={dialogAction !== null}
        onOpenChange={(open) => !open && setDialogAction(null)}
      />

      <SoftDeleteCompanyDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!open && !softDelete.isPending) setDeleteOpen(false)
        }}
        companyName={company.name}
        isPending={softDelete.isPending}
        onConfirm={() => softDelete.mutate(undefined, { onSuccess: () => setDeleteOpen(false) })}
      />

      <PurgeCompanyDialog
        open={purgeOpen}
        onOpenChange={(open) => {
          if (!open && !purge.isPending) setPurgeOpen(false)
        }}
        companyName={company.name}
        isPending={purge.isPending}
        onConfirm={() => purge.mutate(undefined, { onSuccess: () => setPurgeOpen(false) })}
      />
    </div>
  )
}
